import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Grid } from "@mui/material";
import InvestorRequestEntitiesDropdowns from "@investorRequest/components/InvestorRequestEntitiesDropdowns";
import SectionTabs from "@shared/components/SectionTabs/SectionTabs";
import { selectInternalExternalPresence } from "@store/Entities/ducks";
import PaymentMethodSelector from "@investorRequest/components/PaymentMethodSelector";
import InvestorRequestAchFields from "@investorRequest/components/InvestorRequestAchFields";
import InvestorRequestFedWireFields from "@investorRequest/components/InvestorRequestFedWireFields";
import InvestorRequestSwiftFields from "@investorRequest/components/InvestorRequestSwiftFields";
import SubmitAnotherFormDialog from "@investorRequest/components/SubmitAnotherFormDialog";
import {
  investorRequestPreparedDataSelector,
  investorRequestSelector,
  countriesOptionsSelector,
  selectedCountrySelector,
  selectedIntermediaryCountrySelector,
  currentTabInitializedSelector,
} from "@investorRequest/store/selectors";
import { entitledFundsSelector, entitledAccountsSelector } from "@store/Entities/selectors";
import { fetchEntitledAccountsByEntity } from "@store/Entities/thunks";
import {
  updateCustomRequestData,
  loadDraftData,
  setDraftAttachmentInfo,
  updateCommonRequestData,
  clearDraftAttachmentId,
  setActiveInfoTab,
  closeInvestorRequestDialog,
  clearSubmitSuccess,
  dismissPrefillBanner,
  dismissMethodMismatch,
  markFieldEdited,
  markAmbiguousAcknowledged,
  setBankExtractionRailApplied,
} from "@investorRequest/store/ducks";
import { fetchCountriesThunk, getDraftValuesThunk } from "@investorRequest/store/thunks";
import { useAppDispatch, useAppSelector, useMobileLayout } from "@app/hooks";
import { ListOptionBase } from "@shared/components";
import CommonUtils from "@shared/utils/CommonUtils";
import { DraftFormData, InvestorRequestType } from "@investorRequest/models";
import { customFieldsMap, WIRE_INSTRUCTIONS_INTERNAL_FUNDS_ALLOWLIST } from "@investorRequest/constants";
import {
  deriveIsInternalContext,
  isAchPaymentMethodDisabled,
  resolveWirePaymentMethod,
} from "@investorRequest/utils/internalContext";
import BankExtractionPrefillBanner from "@investorRequest/components/BankExtractionPrefillBanner";
import { BankExtractionConfidenceLegend } from "@investorRequest/components/BankExtractionFieldChrome";
import BankExtractionMethodMismatch from "@investorRequest/components/BankExtractionMethodMismatch";
import { BankExtractionFormProvider } from "@investorRequest/components/BankExtractionFormContext";
import {
  reapplyExtractionForPaymentMethod,
  resolveExtractionFields,
} from "@investorRequest/utils/applyExtractionResult";
import { DetectedPaymentMethod } from "@investorRequest/models/bankExtraction";
import { getFormFieldEntries } from "@investorRequest/utils/extractionFieldMapping";

interface WireInstructionsRequestProps {
  extractionReview?: boolean;
}

const WireInstructionsRequest: FunctionComponent<WireInstructionsRequestProps> = ({ extractionReview = false }) => {
  const { funds, accounts, customFields } = useAppSelector(investorRequestPreparedDataSelector);
  const {
    validationWarningShown,
    countries,
    activeInfoTab,
    submitSuccess,
    commonFields,
    sessionAttachments,
    bankExtraction,
  } = useAppSelector(investorRequestSelector);
  const countriesOptions = useAppSelector(countriesOptionsSelector);
  const selectedCountry = useAppSelector(selectedCountrySelector);
  const selectedIntermediaryCountry = useAppSelector(selectedIntermediaryCountrySelector);
  const currentTabInitialized = useAppSelector(currentTabInitializedSelector);
  const dispatch = useAppDispatch();

  // State for controlling the "Submit Another Form" dialog
  const [showSubmitAnotherDialog, setShowSubmitAnotherDialog] = useState(false);

  // Fetch countries on mount

  useEffect(() => {
    if (!countries.loading && countries.data.length === 0) {
      dispatch(fetchCountriesThunk());
    }
  }, [dispatch, countries.loading, countries.data.length]);

  // Do not set bankCountry in state by default; UI selector will default to United States for non‑Swift

  const handleChangeFieldValue = useCallback(
    (fieldName: string) => (value: string) => {
      if (extractionReview && bankExtraction.originalExtractedValues[fieldName] !== undefined) {
        const original = bankExtraction.originalExtractedValues[fieldName];

        if (original !== value) {
          dispatch(markFieldEdited(fieldName));
        }
      }

      dispatch(updateCustomRequestData({ [fieldName]: value }));
    },
    [dispatch, extractionReview, bankExtraction.originalExtractedValues]
  );

  const handleCountrySelect = useCallback(
    (option: ListOptionBase) => {
      dispatch(updateCustomRequestData({ bankCountry: option.id }));
    },
    [dispatch]
  );

  const handleIntermediaryCountrySelect = useCallback(
    (option: ListOptionBase) => {
      dispatch(updateCustomRequestData({ intermediaryBankCountry: option.id }));
    },
    [dispatch]
  );

  const isMobile = useMobileLayout();
  const inputXS = isMobile ? 12 : 6;
  const { hasInternal: hasInternalNonAdmin, hasExternal: hasExternalNonAdmin } = useAppSelector((state) =>
    selectInternalExternalPresence(state as any)
  );

  const entitledFundsMap = useAppSelector(entitledFundsSelector).data;
  const entitledAccountsMap = useAppSelector(entitledAccountsSelector).data;

  // Check if user is admin based on entitled funds' isAdmin field
  const isAdmin = useMemo(() => {
    if (!entitledFundsMap) {
      return false;
    }

    // User is admin if ANY of their entitled funds has isAdmin: true
    const isAdminUser = Object.values(entitledFundsMap).some((fund) => fund.isAdmin === true);

    return isAdminUser;
  }, [entitledFundsMap]);

  // Helper: Check if fund status is closed (fundraisingStatus = "Closed" OR entityType = "Open Ended")
  const isFundStatusClosed = useCallback((fund: { fundraisingStatus?: string | null; entityType?: string | null }) => {
    const fundraisingStatus = (fund.fundraisingStatus || "").toLowerCase();
    const entityType = (fund.entityType || "").toLowerCase();

    return fundraisingStatus === "closed" || entityType === "open ended";
  }, []);

  // Helper: Check if Internal fund is in business exception allowlist
  const isInternalFundAllowlisted = useCallback((fund: { globalId: string }) => {
    return WIRE_INSTRUCTIONS_INTERNAL_FUNDS_ALLOWLIST.includes(fund.globalId as any);
  }, []);

  // For admin users, compute hybrid status based on same criteria as regular users
  // Includes fundraisingStatus/entityType check with allowlist exception for specific internal funds
  const { hasInternal, hasExternal } = (() => {
    if (isAdmin && entitledFundsMap) {
      const fundsArray = Object.values(entitledFundsMap);

      // Internal funds: must be allowlisted OR have closed status
      const hasInt = fundsArray.some((f) => {
        if (f.processArea !== "Internal" || !f.includeInDmltFeed) {
          return false;
        }

        // Business exception: allowlisted funds bypass status check
        if (isInternalFundAllowlisted(f)) {
          return true;
        }

        return isFundStatusClosed(f);
      });

      // External funds: must have closed status (no allowlist exception)
      const hasExt = fundsArray.some((f) => {
        const processArea = String(f.processArea || "")
          .trim()
          .toLowerCase();

        if (!processArea.startsWith("external") || !f.includeInDmltFeed) {
          return false;
        }

        return isFundStatusClosed(f);
      });

      return { hasInternal: hasInt, hasExternal: hasExt };
    }

    return { hasInternal: hasInternalNonAdmin, hasExternal: hasExternalNonAdmin };
  })();

  // Compute hybrid status - used for filtering funds/accounts by tab
  const isHybrid = hasInternal && hasExternal;

  const isInternalContext = deriveIsInternalContext({ hasInternal, hasExternal, activeInfoTab });
  const achDisabled = isAchPaymentMethodDisabled({
    hasInternal,
    hasExternal,
    activeInfoTab,
    fundType: customFields.fundType,
  });
  const effectivePaymentMethod = resolveWirePaymentMethod(customFields.paymentMethod, {
    hasInternal,
    hasExternal,
    activeInfoTab,
    fundType: customFields.fundType,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      const dialogContent = document.querySelector('[data-testid="dialog-content"]');

      if (dialogContent) {
        dialogContent.scrollTop = 0;
      }
    }, 150);

    return () => clearTimeout(timer);
  }, []);

  // IR-UI-Revamp_V1: Set default payment method and fundType; block ACH on External fund contexts
  useEffect(() => {
    const derivedFundType = (() => {
      if (hasInternal && hasExternal) {
        return activeInfoTab === "Internal Fund Information" ? "Internal" : "External";
      }

      if (hasInternal && !hasExternal) {
        return "Internal";
      }

      if (!hasInternal && hasExternal) {
        return "External";
      }

      return undefined;
    })();

    const fundType = customFields.fundType ?? derivedFundType;
    const achDisabledForContext = isAchPaymentMethodDisabled({
      hasInternal,
      hasExternal,
      activeInfoTab,
      fundType,
    });

    const updates: Record<string, string> = {};

    if (derivedFundType && customFields.fundType !== derivedFundType) {
      updates.fundType = derivedFundType;
    }

    const resolvedPaymentMethod = resolveWirePaymentMethod(customFields.paymentMethod, {
      hasInternal,
      hasExternal,
      activeInfoTab,
      fundType,
    });

    if (!customFields.paymentMethod || customFields.paymentMethod === "") {
      updates.paymentMethod = resolvedPaymentMethod;
    } else if (customFields.paymentMethod === "ach" && achDisabledForContext) {
      updates.paymentMethod = "fedwire";
    }

    if (Object.keys(updates).length > 0) {
      dispatch(updateCustomRequestData(updates));
    }
  }, [hasInternal, hasExternal, activeInfoTab, customFields.paymentMethod, customFields.fundType, dispatch]);

  // Bug Fix: Save and restore Swift bank country when switching payment types
  // ACH/FedWire: Always show United States (readonly via disableBankCountry=true)
  // Swift: User can select any country, selection persists per tab
  const prevPaymentMethodRef = useRef<string | undefined>();
  const savedSwiftCountryPerTabRef = useRef<{
    internal?: string;
    external?: string;
  }>({});

  useEffect(() => {
    const paymentMethod = customFields.paymentMethod;
    const prevPaymentMethod = prevPaymentMethodRef.current;
    const bankCountry = customFields.bankCountry;
    const currentTab = activeInfoTab === "Internal Fund Information" ? "internal" : "external";

    // Save Swift country before switching to ACH/FedWire
    if (prevPaymentMethod === "swift" && (paymentMethod === "ach" || paymentMethod === "fedwire") && bankCountry) {
      savedSwiftCountryPerTabRef.current[currentTab] = bankCountry;
      dispatch(updateCustomRequestData({ bankCountry: "" }));
    }

    // Restore Swift country when switching back to Swift
    if ((prevPaymentMethod === "ach" || prevPaymentMethod === "fedwire") && paymentMethod === "swift") {
      const savedSwiftCountry = savedSwiftCountryPerTabRef.current[currentTab];

      if (savedSwiftCountry) {
        dispatch(updateCustomRequestData({ bankCountry: savedSwiftCountry }));
      }
    }

    // Update ref for next render
    prevPaymentMethodRef.current = paymentMethod;
  }, [customFields.paymentMethod, customFields.bankCountry, dispatch, activeInfoTab]);

  // Track previous tab to detect actual tab changes
  const prevActiveTabRef = useRef<string | undefined | null>(null);

  // Tab switching is now handled by Redux setActiveInfoTab action in ducks.ts
  // It automatically saves current tab selections and restores target tab selections
  // No need to manually clear selections here - Redux state management handles it
  useEffect(() => {
    // Update the ref for tracking tab changes (used by draft loading)
    if (isHybrid) {
      prevActiveTabRef.current = activeInfoTab;
    }

    const fundType = (() => {
      if (hasInternal && hasExternal) {
        return activeInfoTab === "Internal Fund Information" ? "Internal" : "External";
      }

      if (hasInternal && !hasExternal) {
        return "Internal";
      }

      if (!hasInternal && hasExternal) {
        return "External";
      }

      // Default fallback
      return "Internal";
    })();

    // Helper function to filter funds/accounts based on current tab
    // IMPORTANT: This MUST use the same logic as the selector to avoid clearing valid selections
    const filterFundsAndAccountsByTab = (fundsToFilter: string[], accountsToFilter: string[]) => {
      // If not hybrid, no filtering needed (single fund type)
      if (!isHybrid || !entitledFundsMap) {
        return { filteredFunds: fundsToFilter, filteredAccounts: accountsToFilter };
      }

      // NOTE: These functions match the isWireInstructionsInternalFund/isWireInstructionsExternalFund helpers in selectors.ts
      // ONLY accept the 4 specific allowlisted internal funds per business requirement
      const matchesInternal = (fundId: string) => {
        const f = entitledFundsMap[fundId];

        if (!f) return false;

        // ONLY accept the 4 specific allowlisted internal funds:
        // - Carlyle Carried Interest (F000122524)
        // - Carlyle Equity Pool (F000122525)
        // - Carlyle Internal Coinvestment (F000122526)
        // - Carlyle Holdings Units (F000122523)
        // All other funds (even if processArea=Internal with closed status) are excluded
        if (!isInternalFundAllowlisted(f)) {
          return false;
        }

        // Defensive checks: Even for allowlisted funds, verify basic criteria
        // to ensure data integrity
        if (f.processArea !== "Internal" || !f.includeInDmltFeed) {
          return false;
        }

        return true;
      };

      const matchesExternal = (fundId: string) => {
        const f = entitledFundsMap[fundId];

        if (!f || !f.includeInDmltFeed) return false;

        const processArea = String(f.processArea || "")
          .trim()
          .toLowerCase();

        if (!processArea.startsWith("external")) return false;

        // Must have closed status (no allowlist exception)
        return isFundStatusClosed(f);
      };

      const isInternalTab = activeInfoTab === "Internal Fund Information";
      const matchFunction = isInternalTab ? matchesInternal : matchesExternal;

      // Filter funds (applies to both regular hybrid users and admin hybrid users)
      const filteredFunds = fundsToFilter.filter((fundId) => matchFunction(fundId));

      // LPC-10220 (AccountsFilterResolve): Admin users - NO account filtering
      // Admins don't have assigned accounts, so they see ALL accounts regardless of tab/fund
      if (isAdmin) {
        return { filteredFunds, filteredAccounts: accountsToFilter };
      }

      // For non-admin hybrid users: Filter accounts based on their associated funds
      // LPC-10220 (AccountsFilterResolve): Keep accounts with null fundGlobalId for draft loading
      const filteredAccounts = accountsToFilter.filter((accountId) => {
        const account = entitledAccountsMap?.[accountId];

        // If account doesn't exist, filter it out
        if (!account) return false;

        // If account has no fundGlobalId, keep it (cannot determine tab association)
        // This is crucial for draft loading where accounts may not have fund associations
        if (!account.fundGlobalId) return true;

        // If account has fundGlobalId, check if it matches current tab
        return matchFunction(account.fundGlobalId);
      });

      return { filteredFunds, filteredAccounts };
    };

    const loadDraft = async () => {
      // LPC-9775: Clear attachment FIRST when loading draft for new tab
      // This prevents attachment from previous tab persisting
      dispatch(updateCommonRequestData({ attachment: null }));
      dispatch(clearDraftAttachmentId());
      const result = await dispatch(getDraftValuesThunk(fundType));

      if (getDraftValuesThunk.fulfilled.match(result) && result.payload) {
        const draftData = result.payload;

        try {
          // The actual JSON is in wireInstructions.instructions
          const instructionsJson = draftData.wireInstructions?.instructions;

          if (!instructionsJson) {
            return;
          }

          const parsedData: DraftFormData = JSON.parse(instructionsJson);

          // Filter selected funds and accounts to match the current tab
          // This ensures Internal funds don't show in External tab and vice versa
          const { filteredFunds: filteredSelectedFunds, filteredAccounts: filteredSelectedAccounts } =
            filterFundsAndAccountsByTab(
              parsedData.commonFields?.selectedFunds || [],
              parsedData.commonFields?.selectedAccounts || []
            );

          // Load the draft data into the form with filtered funds/accounts
          dispatch(
            loadDraftData({
              ...parsedData,
              commonFields: {
                ...parsedData.commonFields,
                selectedFunds: filteredSelectedFunds,
                selectedAccounts: filteredSelectedAccounts,
              },
            })
          );

          // IMPORTANT: Re-set fundType based on current tab after loading draft
          // This ensures the fundType matches the active tab, not what was saved in draft
          dispatch(updateCustomRequestData({ fundType }));

          // Double-check filtering after loading draft to ensure no data from wrong tab slipped through
          if (isHybrid && !isAdmin && entitledFundsMap) {
            const { filteredFunds: finalFilteredFunds, filteredAccounts: finalFilteredAccounts } =
              filterFundsAndAccountsByTab(filteredSelectedFunds, filteredSelectedAccounts);

            if (
              finalFilteredFunds.length !== filteredSelectedFunds.length ||
              finalFilteredAccounts.length !== filteredSelectedAccounts.length
            ) {
              dispatch(
                updateCommonRequestData({
                  selectedFunds: finalFilteredFunds,
                  selectedAccounts: finalFilteredAccounts,
                })
              );
            }
          }

          // LPC-9775: Attachment Priority: Session > Draft
          // Session represents user's current unsaved work, draft represents saved state
          const sessionKey = fundType.toLowerCase() as "internal" | "external";
          const sessionAttachment = sessionAttachments[sessionKey];

          if (sessionAttachment) {
            // Session has attachment → use it (user's latest work takes priority)
            dispatch(
              setDraftAttachmentInfo({
                attachmentId: sessionAttachment.attachmentId || "",
                attachmentName: sessionAttachment.attachmentName,
                attachmentURL: "",
              })
            );

            dispatch(
              updateCommonRequestData({
                attachment: {
                  name: sessionAttachment.attachmentFile.name,
                  type: sessionAttachment.attachmentFile.type as any,
                  base64: sessionAttachment.attachmentFile.base64,
                },
              })
            );
          } else if (parsedData.attachmentId && parsedData.attachmentName) {
            // No session attachment, but draft has attachment → use draft
            dispatch(
              setDraftAttachmentInfo({
                attachmentId: parsedData.attachmentId,
                attachmentName: parsedData.attachmentName,
                attachmentURL: parsedData.AttachmentURL,
              })
            );

            // LPC-9775: DO NOT save draft attachment to session
            // Session is only for user's NEW attachments (not from draft)
            // This prevents the attachment from being restored after user removes it

            // Display the attachment chip (set a placeholder in commonFields.attachment)
            dispatch(
              updateCommonRequestData({
                attachment: {
                  name: parsedData.attachmentName,
                  type: "application/pdf" as any,
                  base64: "", // Empty since we don't have the actual file
                },
              })
            );
          } else {
            // No session, no draft attachment - clear everything
            dispatch(updateCommonRequestData({ attachment: null }));
            dispatch(clearDraftAttachmentId());
          }
        } catch (error) {
          // Silently handle parse errors
        }
      } else {
        // No draft found for this tab — reset form to initial state for this tab
        // This ensures Internal data doesn't show in External tab and vice versa
        // Pass isActualDraft: false to prevent setting lastSavedState baseline
        // (the default payment method will be set after this, and we don't want that to count as a "change")
        dispatch(
          loadDraftData({
            commonFields: {
              selectedFunds: [],
              selectedAccounts: [],
              message: "",
              attachment: null,
            },
            customFields: {
              ...customFieldsMap[InvestorRequestType.UpdateWireInstructions],
              fundType,
            },
            isActualDraft: false, // Mark as initialization, not actual draft
          })
        );
        // Clear any draft attachment info
        dispatch(clearDraftAttachmentId());

        // LPC-9775: Check for session attachment even when no draft exists
        const sessionKey = fundType.toLowerCase() as "internal" | "external";
        const sessionAttachment = sessionAttachments[sessionKey];

        if (sessionAttachment) {
          // Restore session attachment (user's unsaved work)
          dispatch(
            setDraftAttachmentInfo({
              attachmentId: sessionAttachment.attachmentId || "",
              attachmentName: sessionAttachment.attachmentName,
              attachmentURL: "",
            })
          );

          dispatch(
            updateCommonRequestData({
              attachment: {
                name: sessionAttachment.attachmentFile.name,
                type: sessionAttachment.attachmentFile.type as any,
                base64: sessionAttachment.attachmentFile.base64,
              },
            })
          );
        }
      }
    };

    // Tab Independence (AC5): Only load draft if tab hasn't been initialized yet
    // If user has edited fields, edited values take precedence over draft
    if (!currentTabInitialized) {
      loadDraft();
    }
    // LPC-9775: sessionAttachments intentionally excluded from deps
    // We only want to load draft when tab changes, not when session changes
    // Otherwise removing an attachment would trigger re-load and restore it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeInfoTab,
    dispatch,
    hasInternal,
    hasExternal,
    isHybrid,
    isAdmin,
    entitledFundsMap,
    entitledAccountsMap,
    currentTabInitialized,
  ]);

  // Validate selected funds/accounts match current tab criteria
  // This cleans up invalid selections (e.g., if entitlements change while form is open)
  // LPC-10220 (AccountsFilterResolve): Admins are excluded - they see all funds/accounts
  // NOTE: This validation happens AFTER Redux tab restoration, so it won't interfere with tab switching
  useEffect(() => {
    if (!isHybrid || isAdmin || !entitledFundsMap) return;

    const matchesInternal = (fundId: string) => {
      const f = entitledFundsMap[fundId];

      if (!f || !f.includeInDmltFeed) return false;
      if (f.processArea !== "Internal") return false;

      // Business exception: allowlisted funds bypass status check
      if (isInternalFundAllowlisted(f)) return true;

      // Must have closed status
      return isFundStatusClosed(f);
    };

    const matchesExternal = (fundId: string) => {
      const f = entitledFundsMap[fundId];

      if (!f || !f.includeInDmltFeed) return false;

      const processArea = String(f.processArea || "")
        .trim()
        .toLowerCase();

      if (!processArea.startsWith("external")) return false;

      // Must have closed status (no allowlist exception)
      return isFundStatusClosed(f);
    };

    const isInternalTab = activeInfoTab === "Internal Fund Information";
    const matchFunction = isInternalTab ? matchesInternal : matchesExternal;

    // Filter selected funds
    const filteredFunds = (commonFields.selectedFunds || []).filter((fundId) => matchFunction(fundId));

    // LPC-10220 (AccountsFilterResolve): For accounts with null fundGlobalId,
    // we cannot determine which tab they belong to, so keep them selected.
    // This prevents clearing selections for accounts that lack fund association data.
    const filteredAccounts = (commonFields.selectedAccounts || []).filter((accountId) => {
      const account = entitledAccountsMap?.[accountId];

      // If account doesn't exist, filter it out
      if (!account) return false;

      // If account has no fundGlobalId, keep it (cannot determine tab association)
      if (!account.fundGlobalId) return true;

      // If account has fundGlobalId, check if it matches current tab
      return matchFunction(account.fundGlobalId);
    });

    // If any funds/accounts were filtered out, update the state
    if (
      filteredFunds.length !== (commonFields.selectedFunds || []).length ||
      filteredAccounts.length !== (commonFields.selectedAccounts || []).length
    ) {
      dispatch(
        updateCommonRequestData({
          selectedFunds: filteredFunds,
          selectedAccounts: filteredAccounts,
        })
      );
    }
  }, [
    activeInfoTab,
    dispatch,
    isHybrid,
    isAdmin,
    entitledFundsMap,
    entitledAccountsMap,
    commonFields.selectedFunds,
    commonFields.selectedAccounts,
    isFundStatusClosed,
    isInternalFundAllowlisted,
  ]);

  // Do not clear bankCountry on method change; Swift unselected default is UI-only via selector

  // Funds are already filtered by the selector (fundsSelector in selectors.ts)
  // No need to re-filter here - the selector handles all filtering logic
  // including Internal/External, AlpInvest exclusion, and ExcludedFundFamilies
  const filteredFundsOptions = funds;

  // LPC-10220 (AccountsFilterResolve): Track which funds have been fetched to prevent duplicate API calls
  const fetchedFundsRef = useRef<Set<string>>(new Set());

  // LPC-10220 (AccountsFilterResolve): Fetch entitledAccountsByEntity for visible funds
  // This populates entitledAccountIds on funds, needed for proper account filtering
  // NOTE: Admins are EXCLUDED - they don't have assigned accounts and see all accounts
  // from the main entitled accounts API without any fund-based filtering
  useEffect(() => {
    // Skip for admin users - admins see all accounts, no need to fetch by fund
    if (isAdmin) {
      return;
    }

    if (!entitledFundsMap) return;

    // Fetch entitledAccountsByEntity for all visible funds in the dropdown
    filteredFundsOptions.forEach((fundOption) => {
      const fund = entitledFundsMap[fundOption.id];
      const fundId = fundOption.id as string;

      // Only fetch if:
      // 1. Fund exists
      // 2. entitledAccountIds is not yet populated
      // 3. We haven't already fetched this fund (prevent duplicate calls)
      if (fund && !fund.entitledAccountIds && !fetchedFundsRef.current.has(fundId)) {
        // Mark as fetched BEFORE dispatching to prevent race conditions
        fetchedFundsRef.current.add(fundId);

        dispatch(fetchEntitledAccountsByEntity(fundId));
      }
    });
  }, [dispatch, filteredFundsOptions, entitledFundsMap, isAdmin]);

  // After successful submit, show confirmation dialog
  useEffect(() => {
    if (!submitSuccess) return;

    // Show the confirmation dialog
    setShowSubmitAnotherDialog(true);
  }, [submitSuccess]);

  // Handle "Yes" - Submit another form
  const handleSubmitAnotherYes = useCallback(() => {
    setShowSubmitAnotherDialog(false);

    // Clear submitSuccess flag to prevent dialog from appearing on other actions
    dispatch(clearSubmitSuccess());

    // Clear attachment chip state
    dispatch(clearDraftAttachmentId());

    // Reset common and custom fields to defaults for Update Wire Instructions
    dispatch(
      loadDraftData({
        commonFields: { selectedFunds: [], selectedAccounts: [], message: "", attachment: null },
        customFields: customFieldsMap[InvestorRequestType.UpdateWireInstructions],
      })
    );
  }, [dispatch]);

  // Handle "No" - Close the form
  const handleSubmitAnotherNo = useCallback(() => {
    setShowSubmitAnotherDialog(false);

    // Clear submitSuccess flag to prevent dialog from appearing on other actions
    dispatch(clearSubmitSuccess());

    dispatch(closeInvestorRequestDialog());
  }, [dispatch]);

  // Compute filtered accounts based on visible funds globalIds
  // Note: entitledAccountsMap is already declared above
  const selectedAccounts = useAppSelector((state) => state.investorRequest.commonFields.selectedAccounts);
  const selectedAccountsMap = CommonUtils.convertArrayToMap(selectedAccounts);

  // LPC-10220 (AccountsFilterResolve): Accounts are filtered based on the funds' entitledAccountIds
  // (fund → account relationship) and active tab for hybrid users
  const filteredAccountsOptions = useMemo(() => {
    if (!entitledAccountsMap) return accounts;

    // LPC-10220 (AccountsFilterResolve): Admin users see ALL accounts
    // Admins don't have assigned accounts, so they need access to all accounts
    // without any Internal/External/fund-based filtering
    if (isAdmin) {
      const adminAccounts = CommonUtils.convertMapToArray(entitledAccountsMap, (account) => ({
        id: account.globalId,
        label: account.name,
        selected: !!selectedAccountsMap[account.globalId],
      })).sort((a, b) => (a.label || "").localeCompare(b.label || ""));

      return adminAccounts;
    }

    // LPC-10586: For Non Admins : Return empty list if no funds selected (cascading accounts by funds)
    if (!commonFields.selectedFunds || commonFields.selectedFunds.length === 0) {
      return [];
    }

    // LPC-10586: Build allowed account IDs from SELECTED funds only (not all visible)
    const allowedAccountIds = new Set<string>();

    commonFields.selectedFunds.forEach((fundId) => {
      const fund = entitledFundsMap?.[fundId];

      if (fund?.entitledAccountIds) {
        fund.entitledAccountIds.forEach((accountId) => {
          allowedAccountIds.add(accountId);
        });
      }
    });

    // If no entitledAccountIds data available yet:
    // - For hybrid users: use accounts from selector (filtered by Internal/External)
    // - For non-hybrid users: show all entitled accounts
    if (allowedAccountIds.size === 0) {
      if (isHybrid) {
        // For hybrid users, use accounts from selector but rebuild with current selection state
        // to ensure selection works properly
        const rebuiltAccounts = accounts.map((account) => ({
          ...account,
          selected: !!selectedAccountsMap[account.id as string],
        }));

        return rebuiltAccounts;
      }

      // For non-hybrid users, show all entitled accounts
      return CommonUtils.convertMapToArray(entitledAccountsMap, (account) => ({
        id: account.globalId,
        label: account.name,
        selected: !!selectedAccountsMap[account.globalId],
      })).sort((a, b) => (a.label || "").localeCompare(b.label || ""));
    }

    // Filter accounts to only those in the allowed set (from funds' entitledAccountIds)
    const filtered = CommonUtils.convertMapToArray(entitledAccountsMap, (account) => account)
      .filter((account) => allowedAccountIds.has(account.globalId))
      .map((account) => ({
        id: account.globalId,
        label: account.name,
        selected: !!selectedAccountsMap[account.globalId],
      }))
      .sort((a, b) => (a.label || "").localeCompare(b.label || ""));

    return filtered;
  }, [
    entitledAccountsMap,
    accounts,
    isAdmin,
    selectedAccountsMap,
    commonFields.selectedFunds,
    entitledFundsMap,
    isHybrid,
  ]);

  // LPC-10586: Auto-deselect accounts no longer in filtered list (non-admin only)
  useEffect(() => {
    if (isAdmin) return;

    const currentSelected = commonFields.selectedAccounts;

    if (!currentSelected || currentSelected.length === 0) return;

    const validIds = new Set(filteredAccountsOptions.map((a) => a.id));
    const stillValid = currentSelected.filter((id) => validIds.has(id));

    if (stillValid.length !== currentSelected.length) {
      dispatch(updateCommonRequestData({ selectedAccounts: stillValid }));
    }
  }, [filteredAccountsOptions, commonFields.selectedAccounts, dispatch, isAdmin]);

  const extractionPaymentMethodRef = useRef<string | undefined>();

  useEffect(() => {
    if (!extractionReview || bankExtraction.phase !== "review" || !bankExtraction.extractionResult) {
      extractionPaymentMethodRef.current = customFields.paymentMethod;

      return;
    }

    const paymentMethod = customFields.paymentMethod;

    if (!paymentMethod || paymentMethod === extractionPaymentMethodRef.current) {
      return;
    }

    extractionPaymentMethodRef.current = paymentMethod;

    const applied = reapplyExtractionForPaymentMethod(
      bankExtraction.extractionResult,
      paymentMethod as DetectedPaymentMethod,
      customFieldsMap[InvestorRequestType.UpdateWireInstructions]
    );

    dispatch(updateCustomRequestData(applied.customFields));
    dispatch(
      setBankExtractionRailApplied({
        originalExtractedValues: applied.originalExtractedValues,
        fieldConfidence: applied.fieldConfidence,
      })
    );
  }, [extractionReview, bankExtraction.phase, bankExtraction.extractionResult, customFields.paymentMethod, dispatch]);

  const candidatesByField = useMemo(() => {
    if (!bankExtraction.extractionResult) return {};

    const resolvedFields = resolveExtractionFields(
      bankExtraction.extractionResult,
      customFields.paymentMethod || bankExtraction.extractionResult.detectedPaymentMethod
    );

    if (!resolvedFields) return {};

    return getFormFieldEntries(resolvedFields).reduce<Record<string, string[] | null | undefined>>(
      (acc, { formKey, field }) => {
        acc[formKey] = field.candidates ?? null;

        return acc;
      },
      {}
    );
  }, [bankExtraction.extractionResult, customFields.paymentMethod]);

  const showMethodMismatch =
    extractionReview &&
    !bankExtraction.methodMismatchDismissed &&
    bankExtraction.extractionResult?.detectedPaymentMethod &&
    bankExtraction.extractionResult.detectedPaymentMethod !== "unknown" &&
    customFields.paymentMethod &&
    bankExtraction.extractionResult.detectedPaymentMethod !== customFields.paymentMethod;

  const handleSwitchPaymentMethod = useCallback(() => {
    const detected = bankExtraction.extractionResult?.detectedPaymentMethod;

    if (detected && detected !== "unknown") {
      dispatch(updateCustomRequestData({ paymentMethod: detected }));
      dispatch(dismissMethodMismatch());
    }
  }, [bankExtraction.extractionResult, dispatch]);

  const formBody = (
    <>
      <div data-testid="wire-instructions-request" />
      {extractionReview && bankExtraction.filename ? (
        <Grid item xs={12}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} mb={2}>
            <BankExtractionPrefillBanner
              filename={bankExtraction.filename}
              dismissed={bankExtraction.prefillBannerDismissed}
              onDismiss={() => dispatch(dismissPrefillBanner())}
            />
            <BankExtractionConfidenceLegend />
          </Box>
        </Grid>
      ) : null}
      {showMethodMismatch ? (
        <Grid item xs={12}>
          <BankExtractionMethodMismatch
            detectedPaymentMethod={bankExtraction.extractionResult!.detectedPaymentMethod}
            onSwitch={handleSwitchPaymentMethod}
          />
        </Grid>
      ) : null}
      {hasInternal && hasExternal ? (
        <Grid item xs={12}>
          <SectionTabs
            tabs={["Internal Fund Information", "External Fund Information"]}
            activeTab={activeInfoTab || "Internal Fund Information"}
            onTabClick={(tab) => {
              dispatch(setActiveInfoTab(tab));

              // Determine fundType based on the tab
              const fundType = tab === "Internal Fund Information" ? "Internal" : "External";

              if (tab === "Internal Fund Information") {
                dispatch(updateCustomRequestData({ paymentMethod: "ach", fundType }));
              } else if (tab === "External Fund Information") {
                dispatch(updateCustomRequestData({ paymentMethod: "fedwire", fundType }));
              }
            }}
          />
        </Grid>
      ) : null}
      <InvestorRequestEntitiesDropdowns
        required
        accounts={filteredAccountsOptions}
        funds={filteredFundsOptions}
        accountsDisabled={!isAdmin && commonFields.selectedFunds.length === 0}
      />
      <PaymentMethodSelector
        value={effectivePaymentMethod || customFields.paymentMethod || ""}
        isInternalContext={isInternalContext}
        onChange={(val) => {
          // IR-UI-Revamp_V1: If current selection becomes disabled by tab/presence logic, clear it
          const disabled = {
            ach: achDisabled,
            fedwire: false,
            swift: false,
          };

          if (
            (val === "ach" && disabled.ach) ||
            (val === "fedwire" && disabled.fedwire) ||
            (val === "swift" && disabled.swift)
          ) {
            handleChangeFieldValue("paymentMethod")("");
          } else {
            handleChangeFieldValue("paymentMethod")(val);
          }
        }}
        disabledOptions={{
          ach: achDisabled,
          fedwire: false,
          swift: false,
        }}
      />
      <>
        <Grid item container xs={12} columnSpacing={isMobile ? undefined : 7} rowSpacing={isMobile ? 4 : 3}>
          {(effectivePaymentMethod || "ach") === "ach" && (
            <InvestorRequestAchFields
              values={customFields as any}
              countriesOptions={countriesOptions}
              selectedCountry={selectedCountry}
              onSelectCountry={handleCountrySelect}
              onChange={(field, value) => handleChangeFieldValue(field)(value)}
              showErrors={validationWarningShown}
              inputXS={inputXS}
              disableBankCountry={true}
            />
          )}
          {effectivePaymentMethod === "fedwire" && (
            <InvestorRequestFedWireFields
              values={{ ...(customFields as any), fundType: customFields.fundType }}
              countriesOptions={countriesOptions}
              selectedCountry={selectedCountry}
              onSelectCountry={handleCountrySelect}
              selectedIntermediaryCountry={selectedIntermediaryCountry}
              onSelectIntermediaryCountry={handleIntermediaryCountrySelect}
              onChange={(field, value) => handleChangeFieldValue(field)(value)}
              showErrors={validationWarningShown}
              inputXS={inputXS}
              disableBankCountry={true}
            />
          )}
          {effectivePaymentMethod === "swift" && (
            <InvestorRequestSwiftFields
              values={customFields as any}
              countriesOptions={countriesOptions}
              selectedCountry={selectedCountry}
              onSelectCountry={handleCountrySelect}
              selectedIntermediaryCountry={selectedIntermediaryCountry}
              onSelectIntermediaryCountry={handleIntermediaryCountrySelect}
              onChange={(field, value) => handleChangeFieldValue(field)(value)}
              showErrors={validationWarningShown}
              inputXS={inputXS}
              disableBankCountry={false}
            />
          )}
        </Grid>
      </>

      {/* Confirmation dialog for submitting another form */}
      <SubmitAnotherFormDialog
        open={showSubmitAnotherDialog}
        onYes={handleSubmitAnotherYes}
        onNo={handleSubmitAnotherNo}
      />
    </>
  );

  if (extractionReview) {
    return (
      <BankExtractionFormProvider
        isActive
        fieldConfidence={bankExtraction.fieldConfidence}
        extractionEditedFields={bankExtraction.extractionEditedFields}
        ambiguousAcknowledgedFields={bankExtraction.ambiguousAcknowledgedFields}
        candidatesByField={candidatesByField}
        onFieldChange={(fieldName, value) => {
          if (bankExtraction.originalExtractedValues[fieldName] !== undefined) {
            const original = bankExtraction.originalExtractedValues[fieldName];

            if (original !== value) {
              dispatch(markFieldEdited(fieldName));
            }
          }
        }}
        onAmbiguousAcknowledge={(fieldName) => dispatch(markAmbiguousAcknowledged(fieldName))}
      >
        {formBody}
      </BankExtractionFormProvider>
    );
  }

  return formBody;
};

export default WireInstructionsRequest;
